import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  BarChart3,
  Folder,
  HelpCircle,
  Database,
  ChevronDown,
  Settings,
  HardDrive,
  Layers,
  X,
  Flag,
  Banknote,
  Package,
  FileBarChart,
  CalendarClock,
  Network,
  Gift,
  Hammer,
  LayoutGrid,
  CheckCircle,
  Tag,
  Building2,
  FolderKanban,
  ListTodo,
  Boxes,
  Ship,
  SquareCheck,
  Puzzle,
  BarChart2,
  AppWindow,
  Users, // Added for 'User' icon
  CircleDot // Added for a generic 'Module' icon if needed, or choose another relevant icon
} from 'lucide-react';
import { getRequest } from '../services/apiService';

const Sidebar = ({ open, onClose, variant, collapsedWidth, expandedWidth }) => {
  const [mastersOpen, setMastersOpen] = useState(false);
  const [mobileView, setMobileView] = useState(false);
  const location = useLocation();
  const [transactionsOpen, setTransactionsOpen] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [offlineModuleOpen, setOfflineModuleOpen] = useState(false); // New state for Offline Module dropdown
  const [offlineMasterOpen, setOfflineMasterOpen] = useState(false); // New state for Offline Master dropdown
  const [permissions, setPermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);

  // Animation variants
  const sidebarVariants = {
    open: {
      width: expandedWidth,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 30
      }
    },
    closed: {
      width: collapsedWidth,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 30
      }
    }
  };

  const menuItemVariants = {
    open: {
      x: 0,
      opacity: 1,
      transition: {
        x: { stiffness: 1000, velocity: -100 }
      }
    },
    closed: {
      x: 0,
      opacity: 1,
      transition: {
        x: { stiffness: 1000 }
      }
    }
  };

  const dropdownVariants = {
    open: {
      height: "auto",
      opacity: 1,
      transition: {
        height: { duration: 0.3 },
        opacity: { duration: 0.2 }
      }
    },
    closed: {
      height: 0,
      opacity: 0,
      transition: {
        height: { duration: 0.3 },
        opacity: { duration: 0.2 }
      }
    }
  };

  useEffect(() => {
    const checkScreenSize = () => {
      setMobileView(window.innerWidth < 768);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setLoadingPermissions(true);
        const data = await getRequest('/access/get-permissions/');
        setPermissions(data);
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
      } finally {
        setLoadingPermissions(false);
      }
    };

    fetchPermissions();
  }, []);

  // Helper function to get menu item data (sequence and visibility)
  const getMenuItemData = (item, permissionsList) => {
    const findItemInPermissions = (menus, targetLabel) => {
        const normalizedTargetLabel = targetLabel.toLowerCase();

        for (const menu of menus) {
            const normalizedMenuName = menu.name.toLowerCase();

            // Direct match for the menu item's name
            if (normalizedMenuName === normalizedTargetLabel || 
                // Special alias for 'Masters' if the API uses 'Master'
                (normalizedTargetLabel === 'masters' && normalizedMenuName === 'master') ||
                // Special alias for 'Offline Module' if the API uses 'Offline Module'
                (normalizedTargetLabel === 'offline module' && normalizedMenuName === 'offline module')) {

                const hasView = menu.privileges && menu.privileges.some(p => p.name === "View" && p.status);

                // If it's a parent menu with sub-menus, check if any sub-menu is visible
                if (menu.sub_menus && menu.sub_menus.length > 0) {
                    const hasSubMenuVisible = menu.sub_menus.some(subMenu =>
                        subMenu.privileges && subMenu.privileges.some(p => p.name === "View" && p.status)
                    );
                    // Parent is visible if it has view privilege or any sub-menu is visible
                    if (hasView || hasSubMenuVisible) {
                        return { sequence: menu.sequence, hasView: true };
                    }
                } else { // Regular menu item or sub-menu item
                    if (hasView) {
                        return { sequence: menu.sequence, hasView: true };
                    }
                }
            }

            // Recursively search in sub_menus if not found at current level
            if (menu.sub_menus && menu.sub_menus.length > 0) {
                const subItemResult = findItemInPermissions(menu.sub_menus, targetLabel);
                if (subItemResult) {
                    // If a sub-item is found and is visible, its parent (the current 'menu' in this loop) should also be considered visible
                    // This ensures that if 'Offline Master' is visible, 'Offline Module' also becomes visible.
                    if (subItemResult.hasView) {
                        // We are returning the sequence of the *found sub-item*, and setting hasView to true
                        // This propagates the visibility up to the top-level parent correctly for rendering
                        return { sequence: subItemResult.sequence, hasView: true };
                    }
                }
            }
        }
        return null;
    };

    const found = findItemInPermissions(permissionsList, item.label);
    return { ...item, sequence: found?.sequence, visible: found?.hasView || false };
};


  // Define all base menu items (top-level and sub-menus)
  const allBaseTopLevelItems = [
    { label: 'Dashboard', icon: <LayoutDashboard size={18} />, type: 'dropdown' },
    { label: 'Masters', icon: <Database size={20} />, type: 'dropdown' },
    { label: 'Transactions', icon: <Banknote size={20} />, type: 'dropdown' },
    { label: 'Offline Module', icon: <CircleDot size={20} />, type: 'dropdown' }, // Renamed and made dropdown
    { label: 'SetUp', icon: <Hammer size={16} />, path: '/slider' },
    { label: 'Monthly Progress Report', icon: <FileBarChart size={20} />, path: '/documents' },
    { label: 'Finance', icon: <Banknote size={20} />, path: '/financemanagement' },
    { label: 'Holiday', icon: <Gift size={16} />, path: '/holiday' },
    { label: 'Tickets', icon: <Folder size={20} />, path: '/ticket-management-system' },
    { label: 'Milestones', icon: <Flag size={20} />, path: '/milestones' },
    { label: 'Review Meeting', icon: <CalendarClock size={16} />, path: '/reviewmeetings' },
  ];

  const baseDashboardSubItems = [
    { icon: <LayoutDashboard size={18} />, label: 'Analytics Dashboard', path: '/' },
    { icon: <BarChart2 size={18} />, label: 'CPRM Dashboard', path: '/cprm-dashboard' },
    { icon: <BarChart3 size={18} />, label: 'S Curve Data', path: '/scurve-data' },
    { icon: <CheckCircle size={18} />, label: 'WOT Dashboard', path: '/wot-dashboard' },
    { icon: <HardDrive size={18} />, label: 'Equipment Dashboard', path: '/equipment-transaction' },
    { icon: <Layers size={18} />, label: 'Outfit Dashboard', path: '/outfitting-data' },
    { icon: <Layers size={18} />, label: 'WPS Dashboard', path: '/usertoproject' },
    { icon: <Layers size={18} />, label: 'AWPS Dashboard', path: '/awpsdashboard' },
    { icon: <Layers size={18} />, label: 'SNO Dashboard', path: '/snodashboard' },
    { icon: <Layers size={18} />, label: 'NO Dashboard', path: '/nodashboard' },
    { icon: <AppWindow size={18} />, label: 'Inspectors Dashboard', path: '/insdashboard' },
    { icon: <BarChart2 size={18} />, label: 'Legacy Comparison', path: '/legacy-comparison' }, // Added Legacy Comparison
  ];

  const baseMastersSubItems = [
    { icon: <Settings size={16} />, label: 'System', path: '/masters/system' },
    { icon: <Package size={16} />, label: 'Equipment', path: '/masters/equipmentactivity' },
    { icon: <Tag size={16} />, label: 'Category', path: '/masters/category' },
    { icon: <Building2 size={16} />, label: 'Department', path: '/masters/department' },
    { icon: <FolderKanban size={16} />, label: 'Projects', path: '/masters/project' },
    { icon: <ListTodo size={16} />, label: 'Task', path: '/masters/tasks' },
    { icon: <Boxes size={16} />, label: 'Compartment', path: '/masters/compartment' },
    { icon: <Ship size={16} />, label: 'Shipyard', path: '/masters/shipyard' },
    { icon: <LayoutGrid size={16} />, label: 'Directorate', path: '/masters/directrate' },
    { icon: <Package size={16} />, label: 'Block', path: '/masters/block' },
    { icon: <Banknote size={16} />, label: 'Unit', path: '/masters/unit' },
    { icon: <Network size={16} />, label: 'Activity', path: '/masters/activity' },
    { icon: <Flag size={16} />, label: 'Milestone', path: '/masters/milestone' },
  ];

  const baseTransactionSubItems = [
    { icon: <SquareCheck size={16} />, label: 'Block Transaction', path: '/block-transaction' },
    { icon: <Puzzle size={16} />, label: 'Unit Transaction', path: '/unit-transaction' },
    { icon: <CalendarClock size={16} />, label: 'Pert Transaction', path: '/pertupload' },
    { icon: <CalendarClock size={16} />, label: 'Pert Conflict', path: '/pertconflictsresolve' },
  ];

  // New sub-items for Offline Module
  const baseOfflineModuleSubItems = [
    { icon: <LayoutDashboard size={18} />, label: 'Offline Dashboard', path: '/offlinedashboard' }, // Existing offline dashboard path
    { icon: <Database size={20} />, label: 'Offline Master', type: 'dropdown' }, // Nested Master dropdown
  ];

  // New sub-items for Offline Master
  const baseOfflineMasterSubItems = [
    { icon: <Package size={16} />, label: 'Offline Equipment', path: '/masters/system' },
    { icon: <Settings size={16} />, label: 'Offline System', path: '/masters/system' },
    { icon: <Boxes size={16} />, label: 'Offline Compartment', path: '/masters/compartment' },
    { icon: <Building2 size={16} />, label: 'Offline Department', path: '/masters/department' },
    { icon: <FolderKanban size={16} />, label: 'Offline Project', path: '/masters/project' },
    { icon: <Ship size={16} />, label: 'Offline Shipyard', path: '/masters/shipyard' },
    { icon: <Users size={16} />, label: 'Offline User', path: '/offline-masters/user' },
    { icon: <Tag size={16} />, label: 'Offline Category', path: '/masters/category' },
    { icon: <Network size={16} />, label: 'Offline Activity', path: '/masters/equipmentactivity' },
  ];


  const bottomNavItems = [
    { icon: <HelpCircle size={20} />, label: 'Help & Support', path: '/help&support' }
  ];

  // Filter and sort all top-level menu items
  const filteredAndSortedTopLevelItems = permissions.length > 0
    ? allBaseTopLevelItems
        .map(item => getMenuItemData(item, permissions))
        .filter(item => item.visible)
        .sort((a, b) => (a.sequence || Infinity) - (b.sequence || Infinity))
    : allBaseTopLevelItems.map(item => ({ ...item, visible: true })); // Default visible if no permissions

  const filteredAndSortedDashboardSubItems = permissions.length > 0
    ? baseDashboardSubItems
        .map(item => getMenuItemData(item, permissions))
        .filter(item => item.visible)
        .sort((a, b) => (a.sequence || Infinity) - (b.sequence || Infinity))
    : baseDashboardSubItems.map(item => ({ ...item, visible: true }));

  const filteredAndSortedMastersSubItems = permissions.length > 0
    ? baseMastersSubItems
        .map(item => getMenuItemData(item, permissions))
        .filter(item => item.visible)
        .sort((a, b) => (a.sequence || Infinity) - (b.sequence || Infinity))
    : baseMastersSubItems.map(item => ({ ...item, visible: true }));

  const filteredAndSortedTransactionSubItems = permissions.length > 0
    ? baseTransactionSubItems
        .map(item => getMenuItemData(item, permissions))
        .filter(item => item.visible)
        .sort((a, b) => (a.sequence || Infinity) - (b.sequence || Infinity))
    : baseTransactionSubItems.map(item => ({ ...item, visible: true }));

  // Filter and sort Offline Module sub-items
  const filteredAndSortedOfflineModuleSubItems = permissions.length > 0
    ? baseOfflineModuleSubItems
        .map(item => getMenuItemData(item, permissions))
        .filter(item => item.visible)
        .sort((a, b) => (a.sequence || Infinity) - (b.sequence || Infinity))
    : baseOfflineModuleSubItems.map(item => ({ ...item, visible: true }));

  // Filter and sort Offline Master sub-items
  const filteredAndSortedOfflineMasterSubItems = permissions.length > 0
    ? baseOfflineMasterSubItems
        .map(item => getMenuItemData(item, permissions))
        .filter(item => item.visible)
        .sort((a, b) => (a.sequence || Infinity) - (b.sequence || Infinity))
    : baseOfflineMasterSubItems.map(item => ({ ...item, visible: true }));


  // Filter and sort bottom navigation items
  const filteredAndSortedBottomNavItems = permissions.length > 0
  ? bottomNavItems
      .map(item => getMenuItemData(item, permissions))
      .filter(item => item.visible)
      .sort((a, b) => (a.sequence || Infinity) - (b.sequence || Infinity))
  : bottomNavItems.map(item => ({ ...item, visible: true }));

  const isMastersActive = location.pathname.includes('/masters/');
  const isTransactionsActive = filteredAndSortedTransactionSubItems.some(item => location.pathname.startsWith(item.path));
  const isDashboardActive = filteredAndSortedDashboardSubItems.some(item => location.pathname.startsWith(item.path));
  const isOfflineModuleActive = filteredAndSortedOfflineModuleSubItems.some(item => location.pathname.startsWith(item.path)) ||
                                filteredAndSortedOfflineMasterSubItems.some(item => location.pathname.startsWith(item.path));


  const handleMobileItemClick = () => {
    if (mobileView) {
      onClose();
    }
  };

  const renderNavItem = (item, index, isSubMenu = false) => {
    const isActive = location.pathname === item.path || (item.path === '/' && item.label === 'Dashboard' && location.pathname === '/');
    
    // Determine indentation for sub-menus
    const paddingLeftClass = isSubMenu ? 'pl-4' : '';


    if (item.type === 'dropdown') {
      let subItems = [];
      let dropdownOpenState;
      let setDropdownOpenState;
      let currentIsActive;
      let mlClass = "ml-8"; // Default for top-level dropdowns

      switch (item.label) {
        case 'Dashboard':
          subItems = filteredAndSortedDashboardSubItems;
          dropdownOpenState = dashboardOpen;
          setDropdownOpenState = setDashboardOpen;
          currentIsActive = isDashboardActive;
          break;
        case 'Masters':
          subItems = filteredAndSortedMastersSubItems;
          dropdownOpenState = mastersOpen;
          setDropdownOpenState = setMastersOpen;
          currentIsActive = isMastersActive;
          break;
        case 'Transactions':
          subItems = filteredAndSortedTransactionSubItems;
          dropdownOpenState = transactionsOpen;
          setDropdownOpenState = setTransactionsOpen;
          currentIsActive = isTransactionsActive;
          break;
        case 'Offline Module':
          subItems = filteredAndSortedOfflineModuleSubItems;
          dropdownOpenState = offlineModuleOpen;
          setDropdownOpenState = setOfflineModuleOpen;
          currentIsActive = isOfflineModuleActive;
          break;
        case 'Offline Master': // This should match item.label exactly
          subItems = filteredAndSortedOfflineMasterSubItems;
          dropdownOpenState = offlineMasterOpen;
          setDropdownOpenState = setOfflineMasterOpen;
          currentIsActive = filteredAndSortedOfflineMasterSubItems.some(subItem => location.pathname.startsWith(subItem.path));
          mlClass = "ml-12";
          break;
        default:
          return null;
      }
      
      // Only render dropdown if it has visible sub-items
      if (subItems.length > 0) {
        return (
          <motion.li key={item.label}>
            <motion.div
              onClick={() => setDropdownOpenState(!dropdownOpenState)}
              className={`cursor-pointer flex items-center justify-between p-2 rounded-lg hover:bg-indigo-700 transition-colors ${currentIsActive || dropdownOpenState ? 'bg-indigo-700' : ''} ${paddingLeftClass}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center">
                <motion.div className="min-w-8 flex justify-center">
                  {item.icon}
                </motion.div>
                <AnimatePresence>
                  {open && (
                    <motion.span
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="ml-3 whitespace-nowrap overflow-hidden text-ellipsis"
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
              {open && (
                <motion.div animate={{ rotate: dropdownOpenState ? 180 : 0 }}>
                  <ChevronDown size={16} />
                </motion.div>
              )}
            </motion.div>
            <AnimatePresence>
              {dropdownOpenState && (
                <motion.ul
                  variants={dropdownVariants}
                  initial="closed"
                  animate="open"
                  exit="closed"
                  className={`mt-2 ${mlClass} space-y-1`} // Use mlClass for indentation
                >
                  {subItems.map((subItem, subIndex) => renderNavItem(subItem, subIndex, true))} {/* Pass isSubMenu true */}
                </motion.ul>
              )}
            </AnimatePresence>
          </motion.li>
        );
      }
      return null; // Don't render dropdown if no visible sub-items
    } else {
      // Render regular menu item
      return (
        <motion.li
          key={item.label}
          onClick={handleMobileItemClick}
          variants={menuItemVariants}
          custom={index}
        >
          <Link
            to={item.path}
            className={`flex items-center p-2 rounded-lg hover:bg-indigo-700 transition-colors ${isActive ? 'bg-indigo-700' : ''} relative ${paddingLeftClass}`}
          >
            <motion.div
              className="min-w-8 flex justify-center"
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
              style={{ opacity: 1 }}
            >
              {item.icon}
            </motion.div>
            <AnimatePresence>
              {open && (
                <motion.span
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="ml-3 whitespace-nowrap overflow-hidden text-ellipsis"
                >
                  {item.label}
                </motion.span>
              )}
            </AnimatePresence>
          </Link>
        </motion.li>
      );
    }
  };


  // Mobile sidebar layout
  if (mobileView) {
    return (
      <div className={`fixed inset-0 z-50 ${open ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <motion.aside
          initial={{ x: -300 }}
          animate={{ x: 0 }}
          exit={{ x: -300 }}
          className="bg-indigo-800 text-white h-full w-64 max-w-xs transition-all duration-300 ease-in-out flex flex-col justify-between relative"
        >
          <div className="absolute top-4 right-4">
            <motion.button
              onClick={onClose}
              className="p-1 rounded-full hover:bg-indigo-700 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <X size={20} />
            </motion.button>
          </div>
          <nav className="mt-4 flex-grow overflow-y-auto custom-scrollbar px-3">
            <motion.ul className="space-y-1">
              {filteredAndSortedTopLevelItems.map((item, index) => renderNavItem(item, index))}
            </motion.ul>
          </nav>

          <nav className="mb-6">
            <motion.ul className="space-y-2 px-2">
              {filteredAndSortedBottomNavItems.map((item, index) => renderNavItem(item, index))}
            </motion.ul>
          </nav>
        </motion.aside>
      </div>
    );
  }

  return (
    <motion.aside
      initial={open ? "open" : "closed"}
      animate={open ? "open" : "closed"}
      variants={sidebarVariants}
      className="bg-indigo-800 text-white h-full flex flex-col justify-between"
    >
      <nav className="mt-4 flex-grow overflow-y-auto custom-scrollbar px-3">
        <motion.ul className="space-y-1">
          {filteredAndSortedTopLevelItems.map((item, index) => renderNavItem(item, index))}
        </motion.ul>
      </nav>

      <nav className="mb-6">
        <motion.ul className="space-y-2 px-2">
          {filteredAndSortedBottomNavItems.map((item, index) => renderNavItem(item, index))}
        </motion.ul>
      </nav>
    </motion.aside>
  );
};

export default Sidebar;